/**
 * PDP-puntenregel (SHO-9535). Het app-block "Loyalty productpunten" staat als
 * blok ín de USP-popup (`templates/product.json`, USP 3) en rendert daar
 * "Spaar <n> hds premium punten". De popup-knop in de USP-rij heeft bewust geen
 * eigen tekst — het aantal is pas na de app-call bekend — dus spiegelen we de
 * regel van het block naar die knop.
 *
 * Daarnaast: Horizon morpht <main> bij een variant-wissel en block-scripts
 * draaien daarna niet opnieuw. Na de morph vragen we de app zelf om verse
 * schattingen via window.hdsLoyaltyRefreshEstimates() (uitgeleverd in SHO-9524).
 *
 * Maatwerk (configurator) staat hier bewust NIET meer in (SHO-9546). De app
 * leest sinds SHO-9540 de configurator zelf uit — prijs, aantal én de gekozen
 * accessoires — via `selectedProducts` / window.ProductConfiguratorState. Dit
 * bestand corrigeerde de app daarvóór achteraf: het verstopte het block tijdens
 * het `variant:update`-venster en zette het aantal terug ná haar debounce. Die
 * trucs leunden op interne details van de app (de sentinel op `data-product-id`,
 * de class `hds-loyalty__points--loading`) en degradeerden stil zodra daar iets
 * aan veranderde. Niet opnieuw invoeren: wat het thema over maatwerk weet, hoort
 * uit de configurator te komen, niet uit een correctie op de app.
 */
(function () {
  'use strict';

  if (window.__hdsLoyaltyProductPointsBound) return;
  window.__hdsLoyaltyProductPointsBound = true;

  var BLOCK_SELECTOR = '[data-hds-loyalty-estimate]';
  /** Alleen een getal hoort in de USP-regel; "Laden..." of een foutmelding niet. */
  var POINTS_PATTERN = /^\d[\d.,\s]*$/;

  /** @type {MutationObserver | null} */
  var observer = null;
  var syncScheduled = false;
  var refreshScheduled = false;

  /**
   * @param {Element | null} el
   * @returns {string}
   */
  function textOf(el) {
    return el ? (el.textContent || '').trim() : '';
  }

  /**
   * @param {Element} block
   * @returns {string}
   */
  function lineFor(block) {
    var points = textOf(block.querySelector('[data-points]'));
    // Zolang de app nog laadt (of faalt) staat er tekst i.p.v. een getal. Dan
    // tonen we de zin zonder aantal in plaats van "Spaar Laden... punten".
    if (!POINTS_PATTERN.test(points)) points = '';

    return [
      textOf(block.querySelector('.hds-loyalty-product-points__label-before')),
      points,
      textOf(block.querySelector('.hds-loyalty-product-points__label-after')),
    ]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * @param {Element} block
   */
  function syncBlock(block) {
    var host = block.closest('dialog-component');
    if (!(host instanceof HTMLElement)) return;

    var button = host.querySelector('button.popup-link__button');
    if (!(button instanceof HTMLElement)) return;

    var line = lineFor(block);
    if (!line) return;

    var slot = button.querySelector('[data-loyalty-popup-label]');
    if (!slot) {
      slot = document.createElement('span');
      slot.setAttribute('data-loyalty-popup-label', '');
      // Vóór het pijltje, anders staat de caret midden in de regel.
      var icon = button.querySelector('.svg-wrapper');
      if (icon && icon.parentNode === button) {
        button.insertBefore(slot, icon);
      } else {
        button.insertBefore(slot, button.firstChild);
      }
    }
    if (slot.textContent !== line) slot.textContent = line;

    // Elke popup-link rendert dezelfde id, dus binnen deze dialog-component
    // zoeken — document-breed pak je de kop van de eerste USP.
    var heading = host.querySelector('#popup-dialog-heading');
    if (heading && textOf(heading) !== line) heading.textContent = line;
  }

  function syncAll() {
    document.querySelectorAll(BLOCK_SELECTOR).forEach(syncBlock);
  }

  function scheduleSync() {
    if (syncScheduled) return;
    syncScheduled = true;
    requestAnimationFrame(function () {
      syncScheduled = false;
      syncAll();
    });
  }

  /** De app schrijft het aantal pas na haar eigen call; daarop wachten we hier. */
  function observeBlocks() {
    if (!observer) observer = new MutationObserver(scheduleSync);
    // Na een morph zijn de oude nodes losgekoppeld; opnieuw aanhaken.
    observer.disconnect();
    document.querySelectorAll(BLOCK_SELECTOR).forEach(function (block) {
      observer.observe(block, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });
  }

  /**
   * Na een morph is het block vers uit Liquid gerenderd en toont het "—".
   * De app luistert zelf ook op `variant:update`, maar dat event kan de oude —
   * zo weggemorphte — node treffen; daarom hier alsnog om een verse schatting
   * vragen. Komt de app er zelf ook aan toe, dan breekt zij haar eigen vorige
   * verzoek af (AbortController, SHO-9540); een dubbel getal kan dus niet meer
   * ontstaan.
   */
  function afterMorph() {
    // updateMain() roept dit synchroon aan en dispatcht daarna nog twee events
    // die hier uitkomen; alleen de eerste telt.
    if (refreshScheduled) return;
    refreshScheduled = true;

    requestAnimationFrame(function () {
      refreshScheduled = false;
      observeBlocks();
      syncAll();
      if (typeof window.hdsLoyaltyRefreshEstimates === 'function') {
        window.hdsLoyaltyRefreshEstimates();
      }
    });
  }

  // variant-picker.js roept dit aan direct na morph(main).
  window.__hdsLoyaltyAfterMainMorph = afterMorph;
  document.addEventListener('hds:loyalty-dom-reset', afterMorph);

  function start() {
    observeBlocks();
    syncAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
